import { Box, FormControlLabel, FormGroup, Stack, Switch, Tooltip } from '@mui/material';
import React from 'react'
import { useDispatch, useSelector } from 'react-redux';
import DataGrid from '../common/Table/newTable';
import { playerTableHeaderConfig } from '../common/Table/LineupBuilderConfig';
import { Projection } from '../common/EasyProjection/types';
import ProjectionModal from '../common/EasyProjection/ProjectionModal';
import { Refresh } from '@mui/icons-material';
import { setProjectionsRedux, setSlateProjections } from './store/actions';
import { gridFilterModelSelector, useGridApiContext } from '@mui/x-data-grid';
import { IRootState } from '../../store/reducer';
import { Lineup } from './store/types';
import { prepareLineupProjections } from './helpers/specialPositionsHelper';


const PlayerTable = () => {
  const [projections, setProjections] = React.useState<Projection[]>([]);
  const [originalProjections, setOriginalProjections] = React.useState<Projection[]>([]);
  const [customProjections, setCustomProjections] = React.useState<Projection[]>([]);
  const [filteredPositions, setFilteredPositions] = React.useState<string>('');
  const [toggledSalary, setToggledSalary] = React.useState(false);
  const [toggledProj, setToggledProj] = React.useState(true);
  const { selectedSlate } = useSelector((state: any) => state.common);
  const { lineups, selectedLineupId } = useSelector((state: IRootState) => state.lineupBuilder);
  const selectedLineup = lineups.find((lineup) => lineup.id === selectedLineupId) || {} as Lineup;
  const salaryRemaining = selectedSlate?.lineupRules?.teamSalary - selectedLineup?.players?.reduce((partialSum, a) => partialSum + a.salary, 0);

  const { selectedMatchups } = useSelector((state: any) => state.lineupBuilder);
  const sessionStorageKey = `lineupBuilderProjections-${selectedSlate.slateId}`;
  const dispatch = useDispatch();

  const Buttons = () => {
    return (
      <Stack direction="row" gap="8px">
        <Stack direction="row" gap="2px" alignItems="center" style={{ cursor: 'pointer', }} onClick={() => setProj(true)}>Reset <Refresh fontSize='small' /></Stack>
        <ProjectionModal title='Upload Proj' slateId={selectedSlate.slateId?.toString()} onSetProj={(customProjections: Projection[]) => onSetProj(customProjections)} />
      </Stack>
    )
  }

  const onSetProj = (newProjections: Projection[]) => {
    const projectionChanges = newProjections.filter((projection: Projection) => {
      const originalProjection = originalProjections.find((originalProjection: Projection) => originalProjection.nameAndId === projection.nameAndId);
      if (originalProjection) {
        return originalProjection.projection !== projection.projection;
      }
      return false;
    })

    const newProjWithPositions = prepareLineupProjections(newProjections, selectedSlate);

    sessionStorage.setItem(sessionStorageKey, JSON.stringify(projectionChanges));
    setCustomProjections(newProjWithPositions);
  }

  const setProj = async (resetProjections: boolean) => {
    let currentProjections = await fetch(`${process.env.REACT_APP_API_URL}/api/slatedata/projections?SlateId=${selectedSlate.slateId}`).then(res => res.json())
    currentProjections = prepareLineupProjections(currentProjections ?? [], selectedSlate);
    if (resetProjections) {
      sessionStorage.removeItem(sessionStorageKey);
      setCustomProjections([]);
    }

    setOriginalProjections([]);
    const sessionProjections = sessionStorage.getItem(sessionStorageKey);
    if (sessionProjections) {
      const newProjections = prepareLineupProjections(currentProjections, selectedSlate, JSON.parse(sessionProjections));
      setCustomProjections(newProjections);
    }
    else {
      setCustomProjections([]);
    }
    setOriginalProjections(currentProjections)
  }

  React.useEffect(() => {
    if (projections !== undefined && selectedSlate.slateId !== undefined) {
      setProj(false);
    }
    else {
      setProjections([]);
      dispatch(setProjectionsRedux([]))
      setOriginalProjections([]);
    }
  }, [selectedSlate.slateId])

  React.useEffect(() => {
    if (customProjections?.length > 0) {
      setProjections(customProjections);
      dispatch(setProjectionsRedux(customProjections))
    } else {
      setProjections(originalProjections);
      dispatch(setProjectionsRedux(originalProjections))
    }
  }, [customProjections, originalProjections])

  // Publish the whole slate for the pre-sim slate check; `projections` above is narrowed by the matchup filter
  React.useEffect(() => {
    dispatch(setSlateProjections(originalProjections));
  }, [originalProjections])

  const updateSessionStorage = (newProjections: Projection[]) => {
    const projectionChanges = newProjections.filter((projection: Projection) => {
      const originalProjection = originalProjections.find((originalProjection: Projection) => originalProjection.nameAndId === projection.nameAndId);
      if (originalProjection) {
        return originalProjection.projection !== projection.projection;
      }
      return false;
    })
    setCustomProjections(newProjections);
    sessionStorage.setItem(sessionStorageKey, JSON.stringify(projectionChanges));
  }

  const Footer = () => {
    const apiRef = useGridApiContext();
    const currentFilterModel = gridFilterModelSelector(apiRef);

    const onToggleProjections = (checked: boolean) => {
      setToggledProj(checked);
      if (checked) {
        apiRef.current.setFilterModel({
          items: [
            ...currentFilterModel.items,
            { field: 'projection', operator: '>', value: "0" },
          ]
        })
      } else {
        apiRef.current.setFilterModel({
          items: [
            ...currentFilterModel.items.filter((item) => item.field !== 'projection'),
          ]
        })
      }
    }

    const onToggleSalary = (checked: boolean) => {
      setToggledSalary(checked);
      if (checked) {
        apiRef.current.setFilterModel({
          items: [
            ...currentFilterModel.items,
            { field: 'salary', operator: '<', value: salaryRemaining },
          ]
        })
      } else {
        apiRef.current.setFilterModel({
          items: [
            ...currentFilterModel.items.filter((item) => item.field !== 'salary'),
          ]
        })
      }
    }

    React.useEffect(() => {
      if (toggledSalary) {
        apiRef.current.setFilterModel({
          items: [
            ...currentFilterModel.items.filter((item) => item.field !== 'salary'),
            { field: 'salary', operator: '<=', value: salaryRemaining },
          ]
        })
      }
    }, [selectedLineup])

    React.useEffect(() => {
        const filteredPosition = currentFilterModel.items.find(x => x.field === 'canBeAddedToPositions')?.value;
        setFilteredPositions(filteredPosition || '');
    }, [currentFilterModel])

    return (
      <FormGroup sx={{ display: 'flex', flexDirection: 'row' }}>
        <Tooltip title="Hides any player projected to score 0 points">
          <FormControlLabel onChange={(a, checked: boolean) => onToggleProjections(checked)} control={<Switch checked={toggledProj} />} label="Hide 0 Proj." />
        </Tooltip>
        <Tooltip title="Only shows players you can afford with your remaining salary">
          <FormControlLabel onChange={(a, checked: boolean) => onToggleSalary(checked)} control={<Switch checked={toggledSalary}/>} label="Show Affordable Players" />
        </Tooltip>
      </FormGroup>
    )
  }

  React.useEffect(() => {
    const filteredPlayers = originalProjections.filter((player: any) => {
      if (selectedMatchups.length === 0) return true;
      return selectedMatchups.includes(player.team) || selectedMatchups.includes(player.opponent)
    })
    setProjections(filteredPlayers)
    dispatch(setProjectionsRedux(filteredPlayers));
  }, [selectedSlate, selectedMatchups])

  return (
    <Box height={'calc(100vh - 340px)'}>
      <DataGrid
        includeStacks={false}
        lineups={projections}
        headers={playerTableHeaderConfig(originalProjections, projections, updateSessionStorage, selectedSlate, false, lineups, selectedLineupId, filteredPositions)}
        isLoading={projections?.length === 0}
        selectedView="lineupBuilder"
        simType="lineupBuilder"
        setStackView={(view: string) => console.log("")}
        tierlocked={false}
        setPlayerList={() => console.log("")}
        setStackList={() => console.log("")}
        pagination
        rowHeight={40}
        emptyText={"Upload your lineups to start generating."}
        fontSize={14}
        footer={() => <><Footer /></>}
        projectionModal={() => <Buttons />}
        backgroundColor={'rgba(161, 0, 18, 0.15)'}
        initialState={{
          filter: { filterModel: { items: [
            ...(toggledProj ? [{ field: 'projection', operator: '>', value: "0" }] : []),
            ...(toggledSalary ? [{ field: 'salary', operator: '<', value: salaryRemaining }] : [])
          ] } },
        }}
      />
    </Box>
  )
}

export default PlayerTable
